import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { FertilizerProduct } from "@/types/products";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, number, object } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import TextInput from "@/components/inputs/TextInput";
import {
    calculateInjectionRate,
    depthToSpeed,
    productRateToTargetRateN,
    speedToDepth,
    targetRateNToProdutcRate,
} from "@/lib/utils/operations";
import { Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { isNumber } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import ProductAutoComplete from "@/components/inputs/ProductAutoComplete";

const schema = object().shape({
    default_product: number().required(),
    default_n_rate: number().nullable(),
    default_depth: number().nullable(),
    default_max_speed_percentage: number().nullable(),
    default_product_rate: number().nullable(),
    default_speed: number().nullable(),
    injection_rate: number().nullable(),
});

type FormType = InferType<typeof schema>;

function FertilizerPreferences() {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation();
    const { fieldQuery, fieldId } = useFieldDashboard() ?? {};
    const f = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    const [default_product] = f.watch([
        "default_product",
    ]);

    const irrigationSystem = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems[0];
    }, [fieldQuery?.data?.irrigation_systems]);

    const productsQuery = useGetRequest<FertilizerProduct[]>({
        url: `fertilizers/`,
    });
    const selectedProduct = useMemo(() => {
        return productsQuery.data?.find(
            (product) => product.id?.toString() == default_product?.toString(),
        );
    }, [default_product, productsQuery.data]);

    const savePreferencesMutate = useMutateRequest({
        successMessage: t("success.preferences_saved", { ns: "messages" }),
        errorMessage: t("error.failed_to_save_preferences", { ns: "messages" }),
    });

    useEffect(() => {
        const values = f.getValues();
        const product = productsQuery.data?.find(
            (product) =>
                product.id?.toString() ==
                f.getValues()?.default_product?.toString(),
        );

        if (fieldQuery?.data) {
            f.reset({
                default_product:
                    (fieldQuery?.data?.default_product?.toString() as unknown as number) ??
                    null,
                default_n_rate: fieldQuery?.data?.default_n_rate ?? null,
                default_depth: fieldQuery?.data?.default_depth ?? null,
                default_max_speed_percentage:
                    fieldQuery?.data?.default_max_speed_percentage ?? null,

                default_product_rate:
                    targetRateNToProdutcRate(
                        fieldQuery?.data?.default_n_rate,
                        product,
                    ) ?? null,
                default_speed:
                    depthToSpeed(
                        fieldQuery?.data?.default_depth,
                        irrigationSystem,
                    ) ?? null,
                injection_rate: Number(
                    calculateInjectionRate({
                        nRateGalsPerAcre: fieldQuery?.data?.default_n_rate,
                        irrigatedArea: irrigationSystem?.acres,
                        pivotMinRevTime:
                            irrigationSystem?.pivot_revolution_time,
                        pivotSpeedAsPercent: values.default_speed ?? undefined,
                        precision: 2,
                    }) ?? 0,
                ),
            });
        }
    }, [fieldQuery?.data, f, irrigationSystem, productsQuery.data]);

    const onSave = useCallback(
        (data: FormType) => {
            const payload = {
                grower: fieldQuery?.data?.grower,
                name: fieldQuery?.data?.name,
                default_product: data.default_product,
                default_n_rate: data.default_n_rate,
                default_depth: data.default_depth,
                default_max_speed_percentage: data.default_max_speed_percentage,
            };

            savePreferencesMutate.mutate({
                data: {
                    properties: payload,
                },
                method: "patch",
                url: `fields/${fieldId}/`,
            });
        },
        [
            fieldId,
            fieldQuery?.data?.grower,
            fieldQuery?.data?.name,
            savePreferencesMutate,
        ],
    );

    const { formatUnit } = useI18n();

    return (
        <div className="flex flex-col gap-2">
            <FormProvider {...f}>
                <form>
                    <div className="flex flex-col gap-2">
                        <ProductAutoComplete
                            selectedProductId={default_product?.toString() ?? null}
                            setSelectedProductId={(productId) => {
                                if (productId != null && productId !== "") {
                                    f.setValue(
                                        "default_product",
                                        Number(productId),
                                    );
                                    return;
                                }
                            }}
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <TextInput
                                label={`${t(
                                    "fertilizer_preferences.default_n_rate",
                                    {
                                        ns: "setup",
                                    },
                                )} (${formatUnit("lb_ac", "kg_ha")})`}
                                name="default_n_rate"
                                placeholder=" "
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                                hideErrorMessage
                                onChange={(e) => {
                                    if (selectedProduct) {
                                        f.setValue(
                                            "default_product_rate",
                                            targetRateNToProdutcRate(
                                                isNumber(e.target.value)
                                                    ? Number(e.target.value)
                                                    : undefined,
                                                selectedProduct,
                                            ),
                                        );
                                    }
                                }}
                            />
                            <TextInput
                                label={`${t(
                                    "fertilizer_preferences.product_rate",
                                    {
                                        ns: "setup",
                                    },
                                )} (${formatUnit("gal_ac", "L_ha")})`}
                                name="default_product_rate"
                                placeholder=" "
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                                hideErrorMessage
                                onChange={(e) => {
                                    if (selectedProduct) {
                                        f.setValue(
                                            "default_n_rate",
                                            productRateToTargetRateN(
                                                isNumber(e.target.value)
                                                    ? Number(e.target.value)
                                                    : undefined,
                                                selectedProduct,
                                            ),
                                        );
                                    }
                                }}
                            />
                            <TextInput
                                label={t(
                                    "fertilizer_preferences.default_depth",
                                    {
                                        ns: "setup",
                                    },
                                )}
                                name="default_depth"
                                placeholder=" "
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                                hideErrorMessage
                                onChange={(e) => {
                                    if (irrigationSystem) {
                                        f.setValue(
                                            "default_speed",
                                            depthToSpeed(
                                                isNumber(e.target.value)
                                                    ? Number(e.target.value)
                                                    : undefined,
                                                irrigationSystem,
                                            ),
                                        );
                                    }
                                }}
                            />
                            <TextInput
                                label={t("fertilizer_preferences.speed", {
                                    ns: "setup",
                                })}
                                name="default_speed"
                                placeholder=" "
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                                hideErrorMessage
                                onChange={(e) => {
                                    if (irrigationSystem) {
                                        f.setValue(
                                            "default_depth",
                                            speedToDepth(
                                                isNumber(e.target.value)
                                                    ? Number(e.target.value)
                                                    : undefined,
                                                irrigationSystem,
                                            ),
                                        );
                                    }
                                }}
                            />
                            <TextInput
                                label={`${t(
                                    "fertilizer_preferences.injection_rate",
                                    {
                                        ns: "setup",
                                    },
                                )} (${formatUnit("gph", "Lph")})`}
                                name="injection_rate"
                                placeholder=" "
                                isReadOnly
                                isDisabled
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                            />
                            <TextInput
                                label={t(
                                    "fertilizer_preferences.maxiumum_speed",
                                    {
                                        ns: "setup",
                                    },
                                )}
                                name="default_max_speed_percentage"
                                isReadOnly
                                placeholder=" "
                                labelPlacement="outside"
                                size="sm"
                                type="number"
                                decimalPlaces={2}
                                hideErrorMessage
                            />
                        </div>
                        <div className="self-end">
                            <Button
                                size="sm"
                                color="secondary"
                                onPress={() => {
                                    f.trigger();
                                    f.handleSubmit(onSave)();
                                }}
                                startContent={<FontAwesomeIcon icon={faSave} />}
                            >
                                {t("save", {
                                    ns: "common",
                                })}
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
}

export default FertilizerPreferences;
